import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import Loading from 'components/loading';
import CustomTableHead from './tableHead';
import ErrorCard from 'components/errorCard';
import RiveResult from 'components/riveEmpty';
import { CustomFooter } from 'components/customTablePagination';
import { Box, Table, TableRow, TableBody, TableCell, TableContainer } from '@mui/material';

type Props = {
  data: any;
  columns: any;
  page?: number;
  styles?: any;
  limit?: string;
  height?: string;
  error?: boolean;
  nodata?: boolean;
  loading?: boolean;
  noDataText?: string;
  refetch?: () => void;
  handleEvent?: Function;
  disableFooter?: boolean;
  setPage?: (e: number) => void;
  setLimit?: (e: string) => void;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: any,
  orderBy: any
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function CustomTable({
  data,
  page,
  limit,
  error,
  nodata,
  height,
  styles,
  setPage,
  refetch,
  loading,
  columns,
  setLimit,
  noDataText,
  handleEvent,
  disableFooter = false,
}: Props) {
  const customData = data?.results;
  const dataLength = data?.results?.length;
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array?.map((el, index) => [el, index] as [T, number]);
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  const handleRequestSort = (event: any) => {
    const isAsc = orderBy === event && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(event);
  };

  return (
    <Box mt={styles?.mt ? styles.mt : '30px'}>
      <Box height='max-content'>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none !important',
            background: styles?.bg ? styles?.bg : '#F7F8FB',
            height: nodata
              ? '150px'
              : loading || dataLength === 0 || customData === undefined
              ? '350px'
              : dataLength < 13
              ? '100%'
              : `calc(100vh - ${height ? height : '230px'} )`,
          }}>
          <Table
            sx={{
              background: styles?.bg ? styles?.bg : '#F7F8FB',
              minWidth: styles?.minWidth ? styles?.minWidth : '1520px',
              '&.MuiTable-root': {
                borderCollapse: 'unset',
              },
            }}>
            <CustomTableHead
              order={order}
              styles={styles}
              orderBy={orderBy}
              columns={columns}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              <TableRow
                sx={{
                  '.MuiTableCell-body': {
                    border: 'none',
                    padding: '5px 0 5px 0',
                  },
                }}>
                <TableCell colSpan={columns.length}></TableCell>
              </TableRow>
              {error ? (
                <TableRow
                  sx={{
                    '.MuiTableCell-body': {
                      border: 'none',
                      padding: '0',
                    },
                  }}>
                  <TableCell colSpan={columns.length}>
                    <ErrorCard onReset={() => refetch?.()} style={{ marginTop: '0' }} />
                  </TableCell>
                </TableRow>
              ) : loading ? (
                <TableRow
                  sx={{
                    '.MuiTableCell-body': {
                      border: 'none',
                    },
                    '.MuiTableCell-root': {
                      padding: '0',
                    },
                  }}>
                  <TableCell colSpan={columns.length}>
                    <Loading style={{ height: '300px', background: '#fff', marginTop: '10px' }} />
                  </TableCell>
                </TableRow>
              ) : dataLength === 0 || customData === undefined ? (
                <TableRow
                  sx={{
                    '.MuiTableCell-body': {
                      border: 'none',
                    },
                  }}>
                  <TableCell colSpan={columns.length}>
                    {!nodata ? (
                      <RiveResult text={noDataText} />
                    ) : (
                      <Box
                        height={'50px'}
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}>
                        <h1>нет данных</h1>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                stableSort(customData, getComparator(order, orderBy))?.map(
                  (item: any, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        <TableRow
                          sx={{
                            '.MuiTableCell-body': {
                              border: 'none',
                              padding: '5px 0 5px 0',
                              background: styles?.bg ? styles?.bg : '#F7F8FB',
                            },
                          }}>
                          <TableCell colSpan={columns.length}></TableCell>
                        </TableRow>
                        <TableRow
                          key={index}
                          sx={{
                            overflow: 'hidden',
                            borderRadius: '50px',
                            position: 'relative',
                            '&:hover .MuiTableCell-body': {
                              backgroundColor: '#F3F8FC',
                              border: '1px solid #187CD3',
                              borderLeft: 0,
                              borderRight: 0,
                              '&:last-child': {
                                border: '1px solid #187CD3',
                                borderLeft: '0',
                              },
                              '&:first-of-type': {
                                border: '1px solid #187CD3',
                                borderRight: '0',
                              },
                            },
                            '.MuiTableCell-body': {
                              border: 'none',
                              fontWeight: 400,
                              fontSize: '14px',
                              color: '#3f3f3f',
                              padding: '0px 20px',
                              backgroundColor: item?.status ? item.status === 'active' || item.status ==='open' ? styles?.rowBg ? styles?.rowBg : '#fff' : '#f8b1b1' : styles?.rowBg ? styles?.rowBg : '#fff',
                            },
                          }}>
                          {columns?.map((column: any, id: number) => {
                            return (
                              <TableCell
                                sx={{
                                  height: '40px',
                                  cursor: handleEvent === undefined ? 'context-menu' : 'pointer',
                                  '&.MuiTableCell-root': {
                                    border: styles?.borderColor
                                      ? `1px solid ${styles?.borderColor}`
                                      : '1px solid #fff',
                                    borderLeft: 0,
                                    borderRight: 0,
                                    position: column?.end ? 'sticky' : 'static',
                                    right: '0',
                                    // display: column?.end ? 'flex' : 'table-cell',
                                    // justifyContent: column?.end ? 'flex-end' : 'flex-start',
                                    '&:last-child': {
                                      border: styles?.borderColor
                                        ? `1px solid ${styles?.borderColor}`
                                        : '1px solid #fff',
                                      borderLeft: '0',
                                    },
                                    '&:first-of-type': {
                                      border: styles?.borderColor
                                        ? `1px solid ${styles?.borderColor}`
                                        : '1px solid #fff',
                                      borderRight: '0',
                                    },
                                  },
                                }}
                                key={id}
                                onClick={(e: any) => handleEvent?.(item, e)}>
                                {column?.renderCell
                                  ? column?.renderCell(item)
                                  : item[column?.field]}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </React.Fragment>
                    );
                  }
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {error || loading || dataLength === 0 || disableFooter ? null : (
        <CustomFooter
          selectHiddin={data.total > 10}
          setPage={setPage}
          data={data}
          page={page}
          limit={limit}
          setLimit={setLimit}
        />
      )}
    </Box>
  );
}
